import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Dropdown, Empty, Input, InputNumber, Modal, Popconfirm, Select,
  Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  BellOutlined, DeleteOutlined, DownOutlined, UserAddOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  addParticipants, deleteParticipant, getCandidateStudents, getParticipants,
  getStageTeams, notifyFeeApprovals, notifyInvitations, notifyPassportRequest,
  updateParticipant, updateParticipantStatus, updateParticipantConsent, bulkApproveParticipants,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';
import useDebounce from '../../hooks/useDebounce';

const { Text } = Typography;

export const PARTICIPANT_STATUS = {
  candidate:               { label: 'Aday',            color: 'default' },
  participation_approved:  { label: 'Katılım Onaylı',  color: 'processing' },
  participation_declined:  { label: 'Katılmıyor',      color: 'error' },
  fee_approved:            { label: 'Ücret Onaylı',    color: 'warning' },
  fee_declined:            { label: 'Ücretten Vazgeçti', color: 'error' },
  confirmed:               { label: 'Kesinleşti',      color: 'success' },
  cancelled:               { label: 'İptal',           color: 'error' },
};

const CONSENT = {
  pending:  { label: 'Bekliyor',  color: 'default' },
  approved: { label: 'Onayladı',  color: 'success' },
  declined: { label: 'Reddetti',  color: 'error' },
};

const StageParticipantsTab = ({ stage, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [topluOnayliyor, setTopluOnayliyor] = useState(false);
  const [filters, setFilters] = useState({ status: null, team_id: null, search: '' });
  const [teams, setTeams] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  // Toplamlar sunucudan gelir: liste sayfali oldugu icin ekrandaki satirlari
  // toplamak yalnizca gorunen sayfayi toplar ve yanlis sonuc verir.
  const [totals, setTotals] = useState({ students: 0, guests: 0, people: 0 });

  const fetchRows = useCallback(async (targetPage = 1, override = filters) => {
    setLoading(true);
    try {
      const { data } = await getParticipants(stage.id, {
        page: targetPage,
        per_page: 25,
        status: override.status || undefined,
        team_id: override.team_id || undefined,
        search: override.search || undefined,
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
      setPage(data.current_page || targetPage);
      setTotals(data.totals || { students: 0, guests: 0, people: 0 });
    } catch (e) {
      message.error(apiErrorMessage(e, 'Katılımcılar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id, filters]);

  useEffect(() => {
    fetchRows(1, filters);
    getStageTeams(stage.id)
      .then(({ data }) => setTeams((data.data || []).map((st) => st.team).filter(Boolean)))
      .catch(() => setTeams([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.id]);

  const applyFilter = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    fetchRows(1, next);
  };

  const changeStatus = async (record, status) => {
    try {
      await updateParticipantStatus(record.id, { status });
      message.success('Durum güncellendi.');
      fetchRows(page, filters);
    } catch (e) {
      message.error(apiErrorMessage(e));
    }
  };

  const changeConsent = async (record, alan, deger) => {
    try {
      await updateParticipantConsent(record.id, { [alan]: deger });
      message.success('Onay güncellendi.');
      fetchRows(page, filters);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Onay güncellenemedi.'));
    }
  };

  // İki onay sütunu aynı seçiciyi kullanıyor; tek yerde dursun ki biri
  // güncellenip öteki unutulmasın.
  const onaySecici = (record, alan, deger) => (
    <Select
      size="small"
      style={{ width: 118 }}
      value={deger || 'pending'}
      onChange={(v) => changeConsent(record, alan, v)}
      options={Object.entries(CONSENT).map(([k, s]) => ({ value: k, label: s.label }))}
    />
  );

  const topluOnayla = async () => {
    setTopluOnayliyor(true);
    try {
      const { data } = await bulkApproveParticipants(stage.id);
      // Atlanan varsa UYARI: "hepsi onaylandı" sanilmasin. Atlananlar
      // velinin acikca reddettigi ya da iptal edilmis kayitlar.
      if (data.skipped > 0) message.warning(data.message, 8);
      else message.success(data.message);
      fetchRows(page, filters);
      onChanged?.();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Toplu onay yapılamadı.'));
    } finally {
      setTopluOnayliyor(false);
    }
  };

  const notify = async (fn, label) => {
    try {
      const { data } = await fn(stage.id, {});
      message.success(data.message);
      fetchRows(page, filters);
      onChanged?.();
    } catch (e) {
      // Ucret kalemi tanimlanmamissa 422 - sunucunun acikladigi yonlendirme
      message.warning(apiErrorMessage(e, `${label} gönderilemedi.`));
    }
  };

  const columns = [
    {
      title: 'Öğrenci',
      render: (_, r) => {
        const ad = `${r.student?.name || ''} ${r.student?.surname || ''}`.trim();
        if (!ad) return '—';
        // Link YALNIZCA ilişki çözülebildiğinde çiziliyor. `student_id`
        // kolonu silinmiş öğrencide de dolu kalır; ona bağlansaydı adı
        // "—" görünen bir satır tıklanabilir olur ve boş sayfaya giderdi.
        if (!r.student?.id) return ad;
        // Rota `STAFF_ROLES` ile korunuyor; bu sekmeyi görebilen herkes
        // (süper admin, developer admin, yetki verilmiş şube müdürü)
        // personel rolünde, yani kırık link riski yok.
        return (
          <Link to={`/students/${r.student.id}`} className="font-medium">{ad}</Link>
        );
      },
    },
    {
      title: 'Takım',
      dataIndex: ['team', 'name'],
      width: 170,
      render: (v, r) => (
        <Select
          size="small"
          allowClear
          style={{ width: 160 }}
          placeholder="Takım yok"
          value={r.competition_team_id || undefined}
          options={teams.map((t) => ({ value: t.id, label: t.name }))}
          onChange={async (val) => {
            try {
              await updateParticipant(r.id, { competition_team_id: val ?? null });
              message.success('Takım güncellendi.');
              fetchRows(page, filters);
            } catch (e) {
              message.error(apiErrorMessage(e));
            }
          }}
        />
      ),
    },
    {
      title: <Tooltip title="Kaç kişi gelecek — ücret kişi başı hesaplanıyor">Kişi</Tooltip>,
      dataIndex: 'attendee_count',
      width: 80,
      render: (v, r) => (
        <InputNumber
          size="small"
          min={1}
          max={20}
          value={v}
          style={{ width: 62 }}
          onChange={async (val) => {
            if (!val || val === v) return;
            try {
              await updateParticipant(r.id, { attendee_count: val });
              message.success('Kişi sayısı güncellendi. Ücretleri yeniden uygulamayı unutmayın.');
              fetchRows(page, filters);
            } catch (e) {
              message.error(apiErrorMessage(e));
            }
          }}
        />
      ),
    },
    /*
      İKİ ONAY SÜTUNU DA ELLE DEĞİŞTİRİLEBİLİR (21.10.2026).

      Önceden salt okunur `<Tag>` idi ve yalnızca "Durum" üzerinden
      dolaylı değişiyordu. Bitmiş/parası tahsil edilmiş yarışmalar
      sisteme geriye dönük giriliyor; o velilere "gidip onaylayın"
      denemez. Üstelik "Kesinleşti" seçmek onay alanlarına HİÇ
      dokunmuyordu, yani iki sütun sonsuza kadar "Bekliyor" kalıyordu.

      Sunucu bu uçtan gelen değişiklikte IP/cihaz izini siliyor ve nota
      kimin işaretlediğini yazıyor — veli beyanı gibi görünmesin diye.
    */
    {
      title: 'Katılım Onayı',
      dataIndex: 'participation_consent_status',
      width: 130,
      render: (v, r) => onaySecici(r, 'participation_consent_status', v),
    },
    {
      title: 'Ücret Onayı',
      dataIndex: 'fee_consent_status',
      width: 130,
      render: (v, r) => onaySecici(r, 'fee_consent_status', v),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      width: 150,
      render: (v, r) => (
        <Dropdown
          menu={{
            items: Object.entries(PARTICIPANT_STATUS).map(([k, s]) => ({ key: k, label: s.label })),
            onClick: ({ key }) => changeStatus(r, key),
          }}
        >
          <Button size="small">
            <Space size={4}>
              <Tag color={PARTICIPANT_STATUS[v]?.color} style={{ marginInlineEnd: 0 }}>
                {PARTICIPANT_STATUS[v]?.label || v}
              </Tag>
              <DownOutlined style={{ fontSize: 10 }} />
            </Space>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: 'İşlemler',
      width: 70,
      fixed: 'right',
      render: (_, r) => (
        <Popconfirm
          title="Katılımcı listeden çıkarılsın mı?"
          onConfirm={async () => {
            try {
              await deleteParticipant(r.id);
              message.success('Çıkarıldı.');
              fetchRows(page, filters);
            } catch (e) {
              message.warning(apiErrorMessage(e));
            }
          }}
          okText="Çıkar"
          cancelText="Vazgeç"
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {/* Kac kisi gidiyor sorusunun cevabi. Filtre/buton satirina karistirmak
          yerine ust saga alindi: butonlarla yer kavgasi etmiyor, dikey yer de
          kaplamiyor. Filtre uygulandiginda toplam da daralir. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Text type="secondary">
          <Text strong>{totals.students}</Text> öğrenci
          {' · '}
          <Text strong>{totals.guests}</Text> misafir
          {' · '}
          <Text strong>{totals.people}</Text> kişi
        </Text>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Onaylar bildirimle alınıyor"
        description="Veliye zorunlu açılır pencere çıkmaz. Bildirim gönderdiğinizde veli bildirime tıklayıp bu yarışmanın sayfasına gider ve onayını orada verir."
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Öğrenci ara"
          allowClear
          style={{ width: 200 }}
          onSearch={(v) => applyFilter({ search: v })}
        />
        <Select
          placeholder="Durum"
          allowClear
          style={{ width: 170 }}
          value={filters.status}
          onChange={(v) => applyFilter({ status: v })}
          options={Object.entries(PARTICIPANT_STATUS).map(([k, s]) => ({ value: k, label: s.label }))}
        />
        <Select
          placeholder="Takım"
          allowClear
          style={{ width: 170 }}
          value={filters.team_id}
          onChange={(v) => applyFilter({ team_id: v })}
          options={teams.map((t) => ({ value: t.id, label: t.name }))}
        />
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => setAddOpen(true)}>
          Aday Ekle
        </Button>
        <Dropdown
          menu={{
            items: [
              { key: 'invite', icon: <BellOutlined />, label: 'Katılım daveti gönder (adaylara)' },
              { key: 'fee', icon: <BellOutlined />, label: 'Ücret onayı bildirimi gönder' },
              { key: 'passport', icon: <BellOutlined />, label: 'Pasaport bilgisi hatırlatması gönder' },
            ],
            onClick: ({ key }) => {
              if (key === 'invite') notify(notifyInvitations, 'Davet');
              if (key === 'fee') notify(notifyFeeApprovals, 'Ücret onayı bildirimi');
              if (key === 'passport') notify(notifyPassportRequest, 'Pasaport hatırlatması');
            },
          }}
        >
          <Button icon={<BellOutlined />}>Bildirim Gönder <DownOutlined /></Button>
        </Dropdown>
        {/*
          TOPLU ONAY — geriye dönük girilen, bitmiş yarışmalar için.
          Parası tahsil edilmiş bir yarışmanın velilerine "şimdi gidip
          onaylayın" denemiyor; tek tek üç alanı çevirmek 20 kişilik bir
          etapta 60 tıklama demekti.

          Popconfirm ŞART: geri alma düğmesi yok ve tüm listeyi
          değiştiriyor. Metin, redlerin atlanacağını önceden söylüyor ki
          "hepsi onaylandı" sanılmasın.
        */}
        <Popconfirm
          title="Tüm katılımcılar onaylansın mı?"
          description={
            <div style={{ maxWidth: 320 }}>
              Bu etaptaki herkesin katılım ve ücret onayı <b>Onayladı</b>,
              durumu <b>Kesinleşti</b> olur.
              <br />
              Velinin <b>reddettiği</b> ve <b>iptal edilmiş</b> kayıtlar atlanır.
            </div>
          }
          okText="Evet, onayla"
          cancelText="Vazgeç"
          onConfirm={topluOnayla}
        >
          <Button icon={<CheckCircleOutlined />} loading={topluOnayliyor}>
            Tümünü Onayla
          </Button>
        </Popconfirm>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size="small"
        scroll={{ x: 950 }}
        locale={{ emptyText: <Empty description="Henüz aday eklenmemiş." /> }}
        pagination={{
          current: page,
          pageSize: 25,
          total,
          showSizeChanger: false,
          showTotal: (t) => `${t} katılımcı`,
          onChange: (p) => fetchRows(p, filters),
        }}
      />

      <AddCandidatesModal
        stage={stage}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); fetchRows(1, filters); onChanged?.(); }}
      />
    </div>
  );
};

const AddCandidatesModal = ({ stage, open, onClose, onDone }) => {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Sunucu tarafi arama - "per_page yok" hatasina dusmemek icin uc zaten
    // 20 kayitla siniriyor (bkz. UserSearchSelect deseni).
    setFetching(true);
    getCandidateStudents(stage.id, { search: debounced || undefined })
      .then(({ data }) => setOptions(data || []))
      .catch((e) => message.error(apiErrorMessage(e, 'Öğrenciler yüklenemedi.')))
      .finally(() => setFetching(false));
  }, [debounced, open, stage.id]);

  const submit = async () => {
    if (!selected.length) return message.warning('En az bir öğrenci seçin.');
    setSaving(true);
    try {
      const { data } = await addParticipants(stage.id, { student_ids: selected });
      message.success(data.message);
      setSelected([]);
      setSearch('');
      onDone();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Aday Öğrenci Ekle"
      open={open}
      onOk={submit}
      onCancel={onClose}
      okText="Ekle"
      cancelText="Vazgeç"
      confirmLoading={saving}
      destroyOnHidden
    >
      <Text type="secondary">
        Kontenjandan fazla aday ekleyebilirsiniz — kimse elenmez, gelen cevaba
        göre takım ve kategori kurulur.
      </Text>
      <Select
        mode="multiple"
        style={{ width: '100%', marginTop: 12 }}
        placeholder="En az 2 harf yazarak arayın"
        value={selected}
        onChange={setSelected}
        onSearch={setSearch}
        filterOption={false}
        loading={fetching}
        notFoundContent={fetching ? 'Aranıyor…' : 'Sonuç yok'}
        options={options.map((s) => ({ value: s.id, label: `${s.name} ${s.surname}` }))}
      />
    </Modal>
  );
};

export default StageParticipantsTab;
